import { isEqual } from "lodash";
import { Duration } from "luxon";

import { isPlainObject, layConfigPatch, REPLACE_NOTHING } from "@storyboard/config-cascade/cascade";
import { layViewStatePatch } from "@storyboard/dispatch-view/resolve";
import type {
  DispatchConfig,
  DispatchKeymap,
  DispatchViewDefinition,
  DispatchViewState,
  ResolvedDispatchConfig,
} from "@storyboard/dispatch-view/view";
import {
  lenientDispatchKeymapSchema,
  lenientDispatchViewDefinitionSchema,
  lenientDispatchViewStateSchema,
  withBuiltInDispatchViews,
} from "@storyboard/dispatch-view/view";
import type { WebUiConfig } from "@storyboard/web-client";
import {
  DEFAULT_NEW_MESSAGE_SOUND_ID,
  MAX_NEW_MESSAGE_SOUND_VOLUME,
  type NewMessageSoundId,
  type NewMessageSoundScope,
  readNewMessageSoundId,
  readNewMessageSoundVolume,
} from "@/models/newMessageSoundRules";

/**
 * Pure merge/resolve helpers for the web UI settings cascade, kept free of React and the API client so
 * they can be unit-tested directly. The provider in `WebSettingsProvider.tsx` wires these into state,
 * the localStorage cache, and the debounced PATCH.
 */
export interface DriverGridWebSettings {
  columnVisibility: Record<string, boolean>;
  columnOrder: string[];
  columnSizing: Record<string, number>;
  /**
   * User override of the left-pinned columns. Absent means "use the client-computed tag-based default"; present
   * (even if empty) is an explicit user choice. The selection checkbox is always pinned regardless.
   */
  columnPinning?: { left: string[] };
}

export type { DispatchViewState } from "@storyboard/dispatch-view/view";

/**
 * The compiled `web.dispatch` block as this client reads it: `ResolvedDispatchConfig` from `@storyboard/dispatch-view`,
 * the built-in view beneath the cascade's, only the documents that parse. `enabled`, `phoneTagSlug`, `tasks`, and
 * `itemTasks` are workspace-only; the server strips them from membership writes, so they are read here but never sent.
 */
export type DispatchWebSettings = ResolvedDispatchConfig;

/**
 * Sparse update of the Dispatch block, in the server cascade's terms: one layer's `DispatchConfig`.
 *
 * A `views` entry is a whole document the server lays over the stored one key by key; a `viewState` entry is a sparse
 * patch the server lays over the stored entry the same way ({@link layViewStatePatch}); the keymap merges binding by
 * binding. `null` anywhere hands that key back to the layer beneath. Scalars and arrays replace.
 */
export type DispatchWebSettingsUpdate = DispatchConfig;

export interface WebSettings {
  driverGrid: DriverGridWebSettings;
  driverView: { tab: string; sort: string; volumeFilter: string[] };
  layout: { driverFeedPanels: { table: number; feed: number }; panelColumns: { left: number; right: number } };
  hideDriverMessages: boolean;
  userReadOnlyMode: boolean;
  newMessageSoundScope: NewMessageSoundScope;
  newMessageSoundId: NewMessageSoundId;
  /** The ding's volume as an integer percent, so stored values never carry float noise. */
  newMessageSoundVolume: number;
  dispatch: DispatchWebSettings;
  /** When this client applies a waiting build by itself; read only, never written from here (WEB-2417). */
  idleUpdate: IdleUpdateWebSettings;
}

/** The compiled `web.idleUpdate` block, every field present. */
export interface IdleUpdateWebSettings {
  enabled: boolean;
  idleMinutes: number;
  resumeGapMinutes: number;
  bootWindowSeconds: number;
  reloadGuardSeconds: number;
}

/**
 * Deep-partial update. Each nested section merges one level deep over the current value, mirroring the
 * server cascade (maps deep-merge, arrays replace). Pass only the fields that changed.
 */
export interface WebSettingsUpdate {
  driverGrid?: Partial<DriverGridWebSettings>;
  driverView?: Partial<WebSettings["driverView"]>;
  layout?: Partial<WebSettings["layout"]>;
  hideDriverMessages?: boolean;
  userReadOnlyMode?: boolean;
  newMessageSoundScope?: NewMessageSoundScope;
  newMessageSoundId?: NewMessageSoundId;
  newMessageSoundVolume?: number;
  dispatch?: DispatchWebSettingsUpdate;
  /**
   * Dual-written alongside `newMessageSoundScope` so a user who picks a scope here is not silenced on
   * `web/release-259`, which reads only this boolean. Nothing in this client reads it back except
   * `resolveInitialSettings`'s legacy fallback; `mergeWebSettings` copies it onto the settings object
   * and hence into the localStorage cache, which is deliberate — the old client shares that cache key
   * and needs the boolean there for its own first paint. Retired once that release is out.
   */
  playSoundForNewMessages?: boolean;
}

/**
 * Client-side resilience fallback only. The authoritative defaults live in `DefaultWorkspaceConfig`
 * on the backend and arrive compiled on `/userinfo`; this is used solely when `config.web` is absent
 * (e.g. an older server or a failed bootstrap) so the UI never reads `undefined`.
 */
export const FALLBACK_WEB_SETTINGS: WebSettings = {
  driverGrid: {
    columnVisibility: {
      name: true,
      recentMessage: true,
      group: true,
      status: true,
      dutyStatus: true,
      location: true,
      volume: true,
      country: false,
      stateProvince: false,
    },
    columnOrder: [],
    columnSizing: {},
  },
  driverView: { tab: "my", sort: "unread", volumeFilter: [] },
  layout: { driverFeedPanels: { table: 65, feed: 35 }, panelColumns: { left: 70, right: 30 } },
  hideDriverMessages: false,
  userReadOnlyMode: false,
  newMessageSoundScope: "off",
  newMessageSoundId: DEFAULT_NEW_MESSAGE_SOUND_ID,
  newMessageSoundVolume: MAX_NEW_MESSAGE_SOUND_VOLUME,
  dispatch: {
    enabled: false,
    defaultViewId: "default",
    keymap: {},
    showShortcuts: true,
    phoneTagSlug: null,
    tasks: false,
    itemTasks: false,
    views: withBuiltInDispatchViews({}),
    viewState: {},
  },
  idleUpdate: {
    enabled: true,
    idleMinutes: 3,
    resumeGapMinutes: 3,
    bootWindowSeconds: 15,
    reloadGuardSeconds: Duration.fromObject({ minutes: 1 }).as("seconds"),
  },
};

/**
 * Section-aware merge of an update onto a base record. Merges exactly ONE level deep: a section object
 * (e.g. `driverView`) is spread-merged with its existing value, while arrays and scalars replace wholesale.
 * This mirrors the server cascade's map-deep-merge / array-replace behavior for the `web` config sections.
 */
function mergeSections(base: Record<string, unknown>, update: Record<string, unknown>): Record<string, unknown> {
  const next: Record<string, unknown> = { ...base };
  for (const [key, value] of Object.entries(update)) {
    if (value === undefined) continue;
    const current = base[key];
    next[key] = isPlainObject(value) && isPlainObject(current) ? { ...current, ...value } : value;
  }
  return next;
}

/**
 * Merges a Dispatch update onto the compiled block the way the server cascade will, as far as this client can tell.
 *
 * A `views` entry replaces whole here, where the server lays it key by key over the stored one: this client never
 * writes a view, so the two never disagree today, and the PATCH response would re-baseline them if it did. A `null`
 * entry drops, the built-in default staying beneath; a `viewState` patch lays over the compiled entry key by key and
 * a `null` entry drops it; the keymap merges binding by binding and `null` empties it; scalars replace. A `null`
 * reads here as the view's own value, while the server reads it as the workspace's: the PATCH response re-baselines
 * the block, so the two agree as soon as the write lands.
 */
function mergeDispatchSettings(base: DispatchWebSettings, update: DispatchWebSettingsUpdate): DispatchWebSettings {
  const views: Record<string, DispatchViewDefinition | null> = { ...base.views };
  for (const [viewId, view] of Object.entries(update.views ?? {})) {
    if (view === null) delete views[viewId];
    else views[viewId] = view;
  }
  const viewState = { ...base.viewState };
  for (const [viewId, patch] of Object.entries(update.viewState ?? {})) {
    if (patch === null) delete viewState[viewId];
    else viewState[viewId] = layViewStatePatch(viewState[viewId], patch);
  }
  return {
    enabled: update.enabled ?? base.enabled,
    defaultViewId: update.defaultViewId ?? base.defaultViewId,
    keymap: mergeKeymap(base.keymap, update.keymap),
    showShortcuts: update.showShortcuts ?? base.showShortcuts,
    phoneTagSlug: update.phoneTagSlug ?? base.phoneTagSlug,
    tasks: update.tasks ?? base.tasks,
    itemTasks: update.itemTasks ?? base.itemTasks,
    views: withBuiltInDispatchViews(views),
    viewState,
  };
}

function mergeKeymap(base: DispatchKeymap, update: DispatchKeymap | null | undefined): DispatchKeymap {
  if (update === undefined) return base;
  if (update === null) return {};
  return { ...base, ...update };
}

/** Applies a sparse update onto the full settings object. */
export function mergeWebSettings(base: WebSettings, update: WebSettingsUpdate): WebSettings {
  const { dispatch, ...sections } = update;
  const merged = mergeSections(
    base as unknown as Record<string, unknown>,
    sections as Record<string, unknown>,
  ) as unknown as WebSettings;
  return dispatch ? { ...merged, dispatch: mergeDispatchSettings(base.dispatch, dispatch) } : merged;
}

/**
 * Accumulates two sparse updates for the debounced PATCH body: the later laid over the earlier by the cascade's own
 * write rule, so the body is the document the server will store.
 *
 * Objects layer key by key at every depth, arrays and scalars replace whole, and a `null` is kept as the reset it is,
 * so a later `null` entry replaces an earlier patch outright. A patch queued after a `null` entry becomes the body on
 * its own: one body cannot say "reset, then this", and a reset never waits in the queue for a patch to join it, since
 * `useDispatchViewState.reset` flushes it the moment it is queued. The `web` block carries none of the workspace-level
 * whole-value keys, so nothing replaces whole here but arrays and scalars.
 */
export function mergeUpdates(base: WebSettingsUpdate, update: WebSettingsUpdate): WebSettingsUpdate {
  return layConfigPatch(
    REPLACE_NOTHING,
    base as Record<string, unknown>,
    update as Record<string, unknown>,
  ) as WebSettingsUpdate;
}

/**
 * The views of a compiled or cached `dispatch` block that pass the `dispatch-view` schema, keyed by id. Read leniently:
 * a key a newer server added is stripped, and a view that still fails (a workspace typo) is dropped rather than letting
 * the page render a broken table; the id in the map wins over the id inside the definition so a stale key never
 * orphans a view.
 */
export function validDispatchViews(raw: unknown): Record<string, DispatchViewDefinition> {
  if (!isPlainObject(raw)) return {};
  const views: Record<string, DispatchViewDefinition> = {};
  for (const [id, candidate] of Object.entries(raw)) {
    const parsed = lenientDispatchViewDefinitionSchema.safeParse(candidate);
    if (parsed.success) views[id] = { ...parsed.data, id };
  }
  return views;
}

/** The view ids a compiled `dispatch` block carries an explicit `null` for: resets made on another device. */
function resetViewStateIds(raw: unknown): string[] {
  if (!isPlainObject(raw)) return [];
  return Object.entries(raw)
    .filter(([, entry]) => entry === null)
    .map(([viewId]) => viewId);
}

/**
 * The per-view state entries of a compiled or cached `dispatch` block, read leniently: every key that parses is kept
 * and the rest read as absent, so one bad key never costs the member the entry beside it. A `null` entry is a reset
 * and is left out here; `resetViewStateIds` reads those.
 */
export function normalizeViewState(raw: unknown): Record<string, DispatchViewState> {
  if (!isPlainObject(raw)) return {};
  const viewState: Record<string, DispatchViewState> = {};
  for (const [viewId, entry] of Object.entries(raw)) {
    if (!isPlainObject(entry)) continue;
    const parsed = lenientDispatchViewStateSchema.safeParse(entry);
    if (parsed.success) viewState[viewId] = parsed.data;
  }
  return viewState;
}

/**
 * The keymap overrides a compiled or cached block carries, read leniently: every binding that parses is kept, an
 * unknown action or an empty binding is dropped, and a collision is left for the resolver to settle.
 */
export function validDispatchKeymap(raw: unknown): DispatchKeymap | undefined {
  if (!isPlainObject(raw)) return undefined;
  const parsed = lenientDispatchKeymapSchema.safeParse(raw);
  return parsed.success ? parsed.data : undefined;
}

/** A boolean under `key`, or undefined when the block carries none or something else, which is never an error. */
function booleanAt(block: Record<string, unknown> | undefined, key: string): boolean | undefined {
  const value = block?.[key];
  return typeof value === "boolean" ? value : undefined;
}

/** A string under `key`, or undefined when the block carries none or something else. */
function stringAt(block: Record<string, unknown> | undefined, key: string): string | undefined {
  const value = block?.[key];
  return typeof value === "string" ? value : undefined;
}

/**
 * Resolves the Dispatch block with the same precedence as the other sections: server field-by-field over cache over
 * fallback. The views are the server's when its block carries a views map, else the cache's, with the built-in default
 * laid beneath either, so a server that stores no view still yields a view. Every read is lenient: a compiled block
 * this client cannot make sense of yields the fallback for that key, never an error.
 */
function resolveDispatchSettings(
  cached: Partial<DispatchWebSettings> | undefined,
  server: unknown,
): DispatchWebSettings {
  const block = isPlainObject(server) ? server : undefined;
  const configured = isPlainObject(block?.["views"]) ? block["views"] : cached?.views;
  const views = withBuiltInDispatchViews(validDispatchViews(configured));
  // A reset made on another device reaches this one as an explicit server `null`; it must clear the cached entry
  // rather than read as an absence, or the pre-reset layout in the cache would win on every boot.
  const viewState = { ...FALLBACK_WEB_SETTINGS.dispatch.viewState, ...normalizeViewState(cached?.viewState) };
  for (const viewId of resetViewStateIds(block?.["viewState"])) {
    delete viewState[viewId];
  }
  Object.assign(viewState, normalizeViewState(block?.["viewState"]));
  return {
    enabled: booleanAt(block, "enabled") ?? cached?.enabled ?? FALLBACK_WEB_SETTINGS.dispatch.enabled,
    defaultViewId:
      stringAt(block, "defaultViewId") ?? cached?.defaultViewId ?? FALLBACK_WEB_SETTINGS.dispatch.defaultViewId,
    keymap:
      validDispatchKeymap(block?.["keymap"]) ??
      validDispatchKeymap(cached?.keymap) ??
      FALLBACK_WEB_SETTINGS.dispatch.keymap,
    showShortcuts:
      booleanAt(block, "showShortcuts") ?? cached?.showShortcuts ?? FALLBACK_WEB_SETTINGS.dispatch.showShortcuts,
    // The compiled block carries the slug only while the workspace sets one, so a block without it means none; the
    // cache stands in only when no block arrived, or a slug the workspace cleared would come back from it.
    phoneTagSlug: block ? (stringAt(block, "phoneTagSlug") ?? null) : (cached?.phoneTagSlug ?? null),
    tasks: booleanAt(block, "tasks") ?? cached?.tasks ?? FALLBACK_WEB_SETTINGS.dispatch.tasks,
    itemTasks: booleanAt(block, "itemTasks") ?? cached?.itemTasks ?? FALLBACK_WEB_SETTINGS.dispatch.itemTasks,
    views,
    viewState,
  };
}

/**
 * The settings with the Dispatch block re-baselined from the compiled `web` a membership PATCH returned.
 *
 * The server resolves what this client cannot: a `null` this client read as the view's own value is the workspace's
 * default there, and a workspace entry changed since boot shows up here. Edits queued since the PATCH left
 * (`pending`) are laid back over the server's block so nothing the member did in the meantime flickers away. A
 * response carrying no Dispatch block (an older server, a test harness) leaves the settings as they are, and so does
 * one that changes nothing, so a PATCH about another section never re-renders the Dispatch table.
 */
export function withServerDispatch(
  settings: WebSettings,
  serverWeb: unknown,
  pending: DispatchWebSettingsUpdate | undefined,
): WebSettings {
  if (!isPlainObject(serverWeb) || !isPlainObject(serverWeb["dispatch"])) return settings;
  const fromServer = resolveDispatchSettings(undefined, serverWeb["dispatch"]);
  const dispatch = pending ? mergeDispatchSettings(fromServer, pending) : fromServer;
  return isEqual(dispatch, settings.dispatch) ? settings : { ...settings, dispatch };
}

/** The localStorage cache may hold pre-WEB-2257 fields; read for migration only. */
export type CachedWebSettings = Partial<WebSettings> & { playSoundForNewMessages?: boolean };

/**
 * Maps the pre-WEB-2257 boolean onto a scope: ON meant every driver in the workspace, OFF meant silence.
 */
function scopeFromLegacyBoolean(playSound: boolean): NewMessageSoundScope {
  return playSound ? "allDrivers" : "off";
}

/**
 * Resolves the settings to seed React state from. The compiled `/userinfo` `config.web` is the source of
 * truth on every membership swap and wins field-by-field; the localStorage cache is purely a
 * first-contentful-paint optimization, used only for fields the server hasn't provided; the fallback
 * defaults backstop both. Nested sections are merged so a partial server/cache value never drops siblings.
 *
 * `newMessageSoundScope` resolves through both sources of the new key before either source of the legacy
 * `playSoundForNewMessages` boolean: a present new key means the user has actually chosen a scope on a
 * post-WEB-2257 client, so it outranks a boolean the old client may have written since.
 */
export function resolveInitialSettings(cached: CachedWebSettings | null, server: WebUiConfig | undefined): WebSettings {
  return {
    driverGrid: {
      ...FALLBACK_WEB_SETTINGS.driverGrid,
      ...(cached?.driverGrid ?? {}),
      ...(server?.driverGrid ?? {}),
    } as DriverGridWebSettings,
    driverView: { ...FALLBACK_WEB_SETTINGS.driverView, ...(cached?.driverView ?? {}), ...(server?.driverView ?? {}) },
    layout: { ...FALLBACK_WEB_SETTINGS.layout, ...(cached?.layout ?? {}), ...(server?.layout ?? {}) },
    hideDriverMessages:
      server?.hideDriverMessages ?? cached?.hideDriverMessages ?? FALLBACK_WEB_SETTINGS.hideDriverMessages,
    userReadOnlyMode: server?.userReadOnlyMode ?? cached?.userReadOnlyMode ?? FALLBACK_WEB_SETTINGS.userReadOnlyMode,
    newMessageSoundScope: resolveNewMessageSoundScope(cached, server),
    newMessageSoundId:
      readNewMessageSoundId(server?.newMessageSoundId) ??
      readNewMessageSoundId(cached?.newMessageSoundId) ??
      FALLBACK_WEB_SETTINGS.newMessageSoundId,
    newMessageSoundVolume:
      readNewMessageSoundVolume(server?.newMessageSoundVolume) ??
      readNewMessageSoundVolume(cached?.newMessageSoundVolume) ??
      FALLBACK_WEB_SETTINGS.newMessageSoundVolume,
    dispatch: resolveDispatchSettings(cached?.dispatch, server?.dispatch),
    idleUpdate: resolveIdleUpdateSettings(cached?.idleUpdate, server?.idleUpdate),
  };
}

type IdleUpdateNumberKey = Exclude<keyof IdleUpdateWebSettings, "enabled">;

/** The inclusive bounds `WebIdleUpdateConfigSchema` declares for each number field; a new field fails to compile here. */
const IDLE_UPDATE_BOUNDS: Record<IdleUpdateNumberKey, [number, number]> = {
  idleMinutes: [1, 240],
  resumeGapMinutes: [1, 240],
  bootWindowSeconds: [1, 300],
  reloadGuardSeconds: [10, Duration.fromObject({ hours: 1 }).as("seconds")],
};

function isBoolean(value: unknown): value is boolean {
  return typeof value === "boolean";
}

function isIntegerWithin(value: unknown, min: number, max: number): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= min && value <= max;
}

/**
 * Resolves `web.idleUpdate` field by field: server, then cache, then fallback. A number outside the schema's bounds,
 * or not an integer, reads as absent, so a corrupt cache can never make the tab reload in a loop.
 */
function resolveIdleUpdateSettings(cached: unknown, server: unknown): IdleUpdateWebSettings {
  const sources = [server, cached].filter(isPlainObject);
  const fallback = FALLBACK_WEB_SETTINGS.idleUpdate;
  const numberAt = (key: IdleUpdateNumberKey): number => {
    const [min, max] = IDLE_UPDATE_BOUNDS[key];
    const value = sources.map((source) => source[key]).find((candidate) => isIntegerWithin(candidate, min, max));
    return isIntegerWithin(value, min, max) ? value : fallback[key];
  };
  return {
    enabled: sources.map((source) => source["enabled"]).find(isBoolean) ?? fallback.enabled,
    idleMinutes: numberAt("idleMinutes"),
    resumeGapMinutes: numberAt("resumeGapMinutes"),
    bootWindowSeconds: numberAt("bootWindowSeconds"),
    reloadGuardSeconds: numberAt("reloadGuardSeconds"),
  };
}

/**
 * The `newMessageSoundScope` precedence: the new key from either source, then the legacy boolean from
 * either source, then off. Kept separate from `resolveInitialSettings` so the ordering reads as one list.
 */
function resolveNewMessageSoundScope(
  cached: CachedWebSettings | null,
  server: WebUiConfig | undefined,
): NewMessageSoundScope {
  if (server?.newMessageSoundScope != null) return server.newMessageSoundScope;
  if (cached?.newMessageSoundScope != null) return cached.newMessageSoundScope;
  if (server?.playSoundForNewMessages != null) return scopeFromLegacyBoolean(server.playSoundForNewMessages);
  if (cached?.playSoundForNewMessages != null) return scopeFromLegacyBoolean(cached.playSoundForNewMessages);
  return FALLBACK_WEB_SETTINGS.newMessageSoundScope;
}
